import {
  Button,
  Col,
  Divider,
  List,
  notification,
  Row,
  Table,
  UploadFile,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { UploadChangeParam } from 'antd/lib/upload';
import { DownloadCsvTemplateCard, ImportCsvDataCard } from 'components';
import { useState } from 'react';
import { IClientFile, importClient } from 'services/client';
import { readCSV } from 'utilities/file';
import { findDuplicate } from 'utilities/shared';

export const ImportClients: React.FC<{ redirection?: boolean }> = ({ redirection = true }) => {
  const [parsedCSVData, setParsedCSVData] = useState<IClientFile[]>([]);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const { list } = useNavigation();

  const onFileUploadChange = async (info: UploadChangeParam<UploadFile<any>>) => {
    try {
      if (!info.fileList.length) {
        return;
      }

      let rowNumber = 0;
      const file = info.fileList[0].originFileObj!;
      const data: IClientFile[] = await readCSV({
        file,
        transformFunction: (row: any) => {
          rowNumber += 1;

          if (!row.name) {
            throw new Error(`'name' has missing values in row ${rowNumber}`);
          }

          if (!row.clientCode) {
            throw new Error(`'clientCode' has missing values in row ${rowNumber}`);
          }

          const transformedRow: IClientFile = {
            name: row.name,
            clientCode: row.clientCode,
          };
          if (row.email) {
            transformedRow.email = row.email;
          }
          if (row.mobileNumber) {
            transformedRow.mobileNumber = row.mobileNumber;
          }
          if (row.street || row.area || row.city || row.country || row.zip) {
            const address = {
              street: row.street,
              area: row.area,
              city: row.city,
              country: row.country,
              zip: row.zip,
            };
            transformedRow.address = address;
          }

          return transformedRow;
        },
        validateFunction: (row: any) => {
          const requiredFields = ['name', 'clientCode'];
          const keys = Object.keys(row);
          return requiredFields.every((field) => keys.includes(field));
        },
      });

      const duplicateClientCode = findDuplicate(data, 'clientCode');
      const uniqueDuplicates = Array.from(new Set(duplicateClientCode));
      if (duplicateClientCode.length) {
        notification.error({
          message: 'Failed',
          description: `Found ${
            duplicateClientCode.length
          } duplicate values for 'clientCode' - ${uniqueDuplicates.join(', ')}`,
        });
        return;
      }

      setParsedCSVData(data);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
  };

  const onFileRemove = (_file: UploadFile<any>) => {
    setParsedCSVData([]);
  };

  const onSubmitButtonClick = async () => {
    setIsButtonLoading(true);
    try {
      await importClient(parsedCSVData);
      if (redirection) list('clients');
      notification.success({
        message: 'Success',
        description: 'Successfully imported clients',
      });
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsButtonLoading(false);
  };

  const getCsvRowsForTemplate = () => {
    const rows = [
      ['name', 'clientCode', 'email', 'mobileNumber', 'street', 'area', 'city', 'country', 'zip'],
    ];
    const result = [
      {
        name: 'Client 100',
        clientCode: 'C100',
        email: 'c100@mail.com',
        mobileNumber: '00010010010',
        street: 'Rankin Street',
        area: 'Wari',
        city: 'Dhaka',
        country: 'Bangladesh',
        zip: '1200',
      },
      {
        name: 'Client 200',
        clientCode: 'C200',
        email: 'c200@mail.com',
        mobileNumber: '00020020020',
        street: 'Rankin Street',
        area: 'Wari',
        city: 'Dhaka',
        country: 'Bangladesh',
        zip: '1200',
      },
    ];
    result.forEach((elem) => {
      rows.push([
        elem.name,
        elem.clientCode,
        elem.email,
        elem.mobileNumber,
        elem.street,
        elem.area,
        elem.city,
        elem.country,
        elem.zip,
      ]);
    });

    return rows;
  };

  const flatParsedCsvData = parsedCSVData.map(
    ({ name, clientCode, email, mobileNumber, address }) => {
      const flatObject: Record<string, string | undefined> = {
        key: clientCode,
        name,
        clientCode,
        email,
        mobileNumber,
        street: address?.street,
        area: address?.area,
        city: address?.city,
        country: address?.country,
        zip: address?.zip,
      };

      return flatObject;
    },
  );

  const flatCsvObject = flatParsedCsvData[0] || {};
  const columns = [];
  for (const [key] of Object.entries(flatCsvObject)) {
    if (key === 'key') {
      continue;
    }
    columns.push({ title: key, key, dataIndex: key });
  }

  return (
    <List title="Import Clients">
      <Row gutter={[48, 16]} wrap style={{ marginBottom: '2rem' }}>
        <Col xs={11} lg={8}>
          <DownloadCsvTemplateCard
            rows={getCsvRowsForTemplate()}
            title="Download .csv template for import"
          />
        </Col>

        <Col xs={11} sm={11} lg={8}>
          <ImportCsvDataCard onFileUploadChange={onFileUploadChange} onFileRemove={onFileRemove} />
        </Col>
      </Row>

      <Divider />
      {/* CSV Data Table */}
      <Row wrap={false}>
        <Col flex="auto">
          <Table columns={columns} dataSource={flatParsedCsvData} />
        </Col>
      </Row>
      {/* Submit Button */}
      <Row justify="center" align="middle" style={{ marginTop: '1rem' }}>
        <Button
          style={{ width: '300px' }}
          loading={isButtonLoading}
          disabled={!parsedCSVData.length}
          onClick={onSubmitButtonClick}
        >
          Save
        </Button>
      </Row>
    </List>
  );
};
